// Section 16: Children first norm. Established by rationing children ahead of adults (4 uses in
// a row; reset by equal shares or workers ranked ahead of children); lost after 8 seasons without
// reinforcement. Effect once established: adults accept short rations at -1 trust instead of -8.
// Constants: config.normRules, config.norms['children-first'].
import type { Config } from '../../config';
import { mod } from '../../core/modifiers';
import {
  CHILD_GROUPS,
  WORKER_GROUPS,
  foodPrecedent,
  ranksBefore,
  targetPerson,
} from '../../core/content-helpers';
import { isChild } from '../../core/queries';
import { defineNorm } from '../../core/registry';

export function childrenFirst(config: Config) {
  const c = config.norms['children-first'];

  return defineNorm({
    id: 'children-first',
    label: 'Children first',
    section: 'S16',
    establishAfter: config.normRules.establishAfter,
    decayAfter: config.normRules.decayAfter,
    reinforcedBy(ctx, entry) {
      const p = foodPrecedent(ctx, entry);
      if (p === null || p.policy === 'equal') return false;
      // Someone other than a child went short while children were ranked ahead of workers.
      const childGroups: readonly string[] = CHILD_GROUPS;
      const adultsShort = p.short === null || p.short.some((g) => !childGroups.includes(g));
      return adultsShort && ranksBefore(p.order, CHILD_GROUPS, WORKER_GROUPS);
    },
    contradictedBy(ctx, entry) {
      const p = foodPrecedent(ctx, entry);
      return (
        p !== null && (p.policy === 'equal' || ranksBefore(p.order, WORKER_GROUPS, CHILD_GROUPS))
      );
    },
    modifierTargets: ['trustChange'],
    modifierKeys: { trustChange: ['rationed-short'] },
    modifiers(ctx, target) {
      if (!ctx.state.norms['children-first'].established) return [];
      const p = targetPerson(ctx, target);
      if (
        p === null ||
        target.kind !== 'trustChange' ||
        target.cause !== 'rationed-short' ||
        isChild(ctx.cfg, p)
      )
        return [];
      return [mod.set('norm:children-first', c.adultShortTrust, 'children first is accepted')];
    },
  });
}
